/** @jsxImportSource @emotion/react */
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { tariffDetailsStyles } from './tariff-details.styles';
import { DaySwitcher } from '../day-switcher/day-switcher.component';

type TariffPricesPeriod = {
    startHours: number;
    startMinutes: number;
    endHours: number;
    endMinutes: number;
    days: number[];
    price: number;
};

type TariffTieredPricesPeriod = {
    quantity: number;
    price: number;
};

type TariffIdleFee = {
    enabled?: boolean;
    graceMinutes?: number;
    price?: number;
};

type TariffPricing = {
    type?: 'fixed' | 'tiered';
    pricingOption?: 'free' | 'kwh' | 'hour';
    tieredPricingOption?: 'kwh' | 'hour';
    priceForHour?: number;
    priceForKwh?: number;
    pricesPeriods?: TariffPricesPeriod[];
    tieredPricesPeriods?: TariffTieredPricesPeriod[];
    securityDepositAmount?: number;
    activationFee?: number;
    reservationFee?: number;
    idleFee?: TariffIdleFee;
    tax?: number;
};

type Props = {
    tariff?: TariffPricing;
    currency: string;
    day: number; // 0..6 (Mon..Sun)
    onDayChange: (day: number) => void;
    disableSessionLimit: boolean;
    minimalChargeAmount?: number;
};

const unitForPricingOption = (pricingOption?: string) => {
    if (pricingOption === 'hour') return 'h';
    return 'kWh';
};

const formatMinutesHM = (totalMinutes: number) => {
    const minutes = Math.max(0, Math.round(totalMinutes));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h <= 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const formatTierQuantityLabel = (qty: number, tieredPricingOption?: 'kwh' | 'hour') => {
    if (tieredPricingOption === 'hour') {
        const minutes = qty * 60;
        return formatMinutesHM(minutes);
    }
    const pretty = Number.isInteger(qty) ? String(qty) : String(qty);
    return `${pretty} kWh`;
};

const formatTierRangeLabel = (
    from: number,
    to: number,
    tieredPricingOption?: 'kwh' | 'hour'
) => {
    if (tieredPricingOption === 'hour') {
        const f = formatMinutesHM(from * 60);
        const t = formatMinutesHM(to * 60);
        return `${f}\u2013${t}`;
    }
    const f = Number.isInteger(from) ? String(from) : String(from);
    const t = Number.isInteger(to) ? String(to) : String(to);
    return `${f}\u2013${t}`;
};

const formatMoney = (amountMinor: number | undefined, currency: string) => {
    if (amountMinor === undefined || amountMinor === null) return undefined;
    return `${Number(amountMinor / 100).toFixed(2)} ${currency}`;
};

const formatTime = (hours: number, minutes: number) => {
    const h = ((hours % 24) + 24) % 24;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 === 0 ? 12 : h % 12;
    const mm = String(Math.max(0, Math.min(59, minutes))).padStart(2, '0');
    return `${hh}:${mm} ${ampm}`;
};

const formatPeriodFromMinutes = (startMin: number, endMin: number) => {
    const sH = Math.floor(startMin / 60);
    const sM = startMin % 60;
    const eH = Math.floor(endMin / 60);
    const eM = endMin % 60;
    return `${formatTime(sH, sM)} \u2013 ${formatTime(eH, eM)}`;
};

const normalizePeriods = (periods: TariffPricesPeriod[]) => {
    return [...periods]
        .sort((a, b) => {
            const aStart = a.startHours * 60 + a.startMinutes;
            const bStart = b.startHours * 60 + b.startMinutes;
            if (aStart !== bStart) return aStart - bStart;
            const aEnd = a.endHours * 60 + a.endMinutes;
            const bEnd = b.endHours * 60 + b.endMinutes;
            if (aEnd !== bEnd) return aEnd - bEnd;
            return (a.price ?? 0) - (b.price ?? 0);
        })
        .map(p => `${p.startHours}:${p.startMinutes}-${p.endHours}:${p.endMinutes}:${p.price}`)
        .join('|');
};

const periodsForDay = (periods: TariffPricesPeriod[], day: number) =>
    periods.filter(p => Array.isArray(p.days) && p.days.includes(day));

type Segment = { startMin: number; endMin: number; price: number };

const toMinuteOfDay = (hours: number, minutes: number) => {
    const h = Math.max(0, Math.min(24, hours));
    const m = Math.max(0, Math.min(59, minutes));
    return Math.min(24 * 60, h * 60 + m);
};

const buildSegmentsForDay = (periods: TariffPricesPeriod[], day: number, basePrice?: number) => {
    const result: Segment[] = [];

    const dayPeriods = periodsForDay(periods, day)
        .map(p => ({
            startMin: toMinuteOfDay(p.startHours, p.startMinutes),
            endMin: toMinuteOfDay(p.endHours, p.endMinutes),
            price: p.price ?? 0,
        }))
        .filter(p => p.endMin > p.startMin)
        .sort((a, b) => a.startMin - b.startMin);

    const base = basePrice ?? 0;
    let cursor = 0;

    for (const p of dayPeriods) {
        const start = Math.max(0, Math.min(24 * 60, p.startMin));
        const end = Math.max(0, Math.min(24 * 60, p.endMin));
        if (start > cursor) {
            result.push({ startMin: cursor, endMin: start, price: base });
        }
        result.push({ startMin: start, endMin: end, price: p.price });
        cursor = Math.max(cursor, end);
    }

    if (cursor < 24 * 60) {
        result.push({ startMin: cursor, endMin: 24 * 60, price: base });
    }

    // Merge adjacent segments with same price
    const merged: Segment[] = [];
    for (const seg of result) {
        const prev = merged.at(-1);
        if (prev && prev.price === seg.price && prev.endMin === seg.startMin) {
            prev.endMin = seg.endMin;
        } else {
            merged.push({ ...seg });
        }
    }

    return merged;
};

const normalizeSegments = (segments: Segment[]) =>
    segments.map(s => `${s.startMin}-${s.endMin}:${s.price}`).join('|');

const sameScheduleAllWeek = (periods: TariffPricesPeriod[], basePrice?: number) => {
    const normalized = new Set<string>();
    for (let d = 0; d < 7; d++) {
        normalized.add(normalizeSegments(buildSegmentsForDay(periods, d, basePrice)));
    }
    return normalized.size === 1;
};

export const TariffDetails: FC<Props> = ({
    tariff,
    currency,
    day,
    onDayChange,
    disableSessionLimit,
    minimalChargeAmount,
}) => {
    const intl = useIntl();
    const type = tariff?.type ?? 'fixed';
    const pricingOption = tariff?.pricingOption ?? 'kwh';

    const unit = unitForPricingOption(pricingOption);

    const fixedPrice = useMemo(() => {
        if (pricingOption === 'free') return undefined;
        if (pricingOption === 'hour') return tariff?.priceForHour;
        return tariff?.priceForKwh;
    }, [pricingOption, tariff?.priceForHour, tariff?.priceForKwh]);

    const pricesPeriods = Array.isArray(tariff?.pricesPeriods) ? tariff?.pricesPeriods ?? [] : [];
    const tieredPeriods = Array.isArray(tariff?.tieredPricesPeriods) ? tariff?.tieredPricesPeriods ?? [] : [];

    const showTiered = type === 'tiered' && tieredPeriods.length > 0;

    const showPeriods = !showTiered && pricesPeriods.length > 0 && pricingOption !== 'free';
    const showDaySwitcher = useMemo(() => {
        if (!showPeriods) return false;
        return !sameScheduleAllWeek(pricesPeriods, fixedPrice);
    }, [fixedPrice, pricesPeriods, showPeriods]);

    const segmentsToRender = useMemo(() => {
        if (pricingOption === 'free') return [];
        if (!showPeriods) return [];
        const base = fixedPrice ?? 0;
        const d = showDaySwitcher ? day : 0;
        return buildSegmentsForDay(pricesPeriods, d, base);
    }, [day, fixedPrice, pricesPeriods, pricingOption, showDaySwitcher, showPeriods]);

    const chargingPriceRows = useMemo(() => {
        if (pricingOption === 'free') return [];
        if (showTiered) return [];
        if (showPeriods) {
            return segmentsToRender.map(s => ({
                name: formatPeriodFromMinutes(s.startMin, s.endMin),
                value: `${Number((s.price ?? 0) / 100).toFixed(2)} ${currency}/${unit}`,
            }));
        }
        if (fixedPrice !== undefined && fixedPrice !== null) {
            return [
                {
                    name: pricingOption === 'hour' ? 'Per hour' : 'Per kWh',
                    value: `${Number(fixedPrice / 100).toFixed(2)} ${currency}/${unit}`,
                },
            ];
        }
        return [];
    }, [currency, fixedPrice, pricingOption, segmentsToRender, showPeriods, showTiered, unit]);

    const minimalAmountRow = useMemo(
        () =>
            minimalChargeAmount
                ? [
                      {
                          name: intl.formatMessage({ id: 'connector.descriptions.minimalAmount' }),
                          value: `${Number(minimalChargeAmount / 100).toFixed(2)} ${currency}`,
                      },
                  ]
                : [],
        [currency, intl, minimalChargeAmount],
    );

    const additionalFeesRows = useMemo(() => {
        const rows: { name: string; value: string }[] = [];

        const activationFee = formatMoney(tariff?.activationFee, currency);
        if (activationFee)
            rows.push({
                name: intl.formatMessage({ id: 'tariffDetails.fees.connectionFee' }),
                value: activationFee,
            });

        const reservationFee = formatMoney(tariff?.reservationFee, currency);
        if (reservationFee)
            rows.push({
                name: intl.formatMessage({ id: 'tariffDetails.fees.reservationFee' }),
                value: reservationFee,
            });

        if (disableSessionLimit) {
            const preAuth = formatMoney(tariff?.securityDepositAmount, currency);
            if (preAuth)
                rows.push({
                    name: intl.formatMessage({ id: 'tariffDetails.fees.preAuthorizedFee' }),
                    value: preAuth,
                });
        }

        // if (tariff?.tax !== undefined && tariff?.tax !== null) {
        //     rows.push({
        //         name: intl.formatMessage({ id: 'tariffDetails.fees.salesTax' }),
        //         value: `${tariff.tax}%`,
        //     });
        // }

        if (tariff?.idleFee?.enabled) {
            if (tariff.idleFee.graceMinutes !== undefined && tariff.idleFee.graceMinutes !== null) {
                rows.push({
                    name: intl.formatMessage({ id: 'connector.info.gracePeriod' }),
                    value: `${tariff.idleFee.graceMinutes} min`,
                });
            }
            const idleFeeRate = tariff.idleFee.price;
            if (idleFeeRate !== undefined && idleFeeRate !== null)
                rows.push({
                    name: intl.formatMessage({ id: 'connector.info.idleFee' }),
                    value: intl.formatMessage(
                        { id: 'connector.info.idleFeeRateAfterGracePeriod' },
                        { rate: Number(idleFeeRate / 100).toFixed(2), currency }
                    ),
                });
        }

        return rows;
    }, [
        currency,
        disableSessionLimit,
        intl,
        tariff?.activationFee,
        tariff?.idleFee,
        tariff?.reservationFee,
        tariff?.securityDepositAmount,
        tariff?.tax,
    ]);

    if (!tariff) return null;

    const tieredRows = useMemo(() => {
        if (!showTiered) return [];
        const sorted = [...tieredPeriods].sort((a, b) => (a?.quantity ?? 0) - (b?.quantity ?? 0));
        const rows: { name: string; value: string }[] = [];
        const perUnit = unitForPricingOption(pricingOption);

        const firstStartQ = sorted?.[0]?.quantity ?? 0;
        const hasFixedBeforeFirstTier =
            fixedPrice !== undefined &&
            fixedPrice !== null &&
            Number.isFinite(fixedPrice as number) &&
            firstStartQ > 0;

        if (hasFixedBeforeFirstTier) {
            const segmentLabel = formatTierQuantityLabel(firstStartQ, tariff?.tieredPricingOption);
            const rangeLabel = ` (${formatTierRangeLabel(0, firstStartQ, tariff?.tieredPricingOption)})`;
            rows.push({
                name: intl.formatMessage(
                    { id: 'tariffDetails.tiered.first' },
                    { segment: segmentLabel, range: rangeLabel }
                ),
                value: `${Number(((fixedPrice ?? 0) as number) / 100).toFixed(2)} ${currency}/${perUnit}`,
            });
        }

        if (sorted.length >= 2) {
            for (let i = 0; i < sorted.length - 1; i++) {
                const startQ = i === 0 && !hasFixedBeforeFirstTier ? 0 : (sorted?.[i]?.quantity ?? 0);
                const endQ = sorted?.[i + 1]?.quantity ?? startQ;
                const delta = Math.max(0, endQ - startQ);
                const isFirst = rows.length === 0;
                const segmentLabel = formatTierQuantityLabel(delta, tariff?.tieredPricingOption);
                const rangeLabel = ` (${formatTierRangeLabel(startQ, endQ, tariff?.tieredPricingOption)})`;
                rows.push({
                    name: intl.formatMessage(
                        { id: isFirst ? 'tariffDetails.tiered.first' : 'tariffDetails.tiered.next' },
                        { segment: segmentLabel, range: rangeLabel }
                    ),
                    value: `${Number(((sorted[i].price ?? 0) as number) / 100).toFixed(2)} ${currency}/${perUnit}`,
                });
            }
        }

        if (sorted.length > 0) {
            const last = sorted.at(-1);
            const lastQ = last?.quantity ?? 0;
            rows.push({
                name: intl.formatMessage(
                    { id: 'tariffDetails.tiered.after' },
                    { quantity: formatTierQuantityLabel(lastQ, tariff?.tieredPricingOption) }
                ),
                value: `${Number(((last?.price ?? 0) as number) / 100).toFixed(2)} ${currency}/${perUnit}`,
            });
        }

        return rows;
    }, [currency, fixedPrice, intl, pricingOption, showTiered, tariff?.tieredPricingOption, tieredPeriods]);

    return (
        <div css={tariffDetailsStyles.container}>
            {showDaySwitcher && (
                <div css={tariffDetailsStyles.daySwitcherContainer}>
                    <DaySwitcher value={day} onChange={onDayChange} />
                </div>
            )}

            {showTiered ? (
                <div>
                    <div css={tariffDetailsStyles.blockTitle}>
                        {intl.formatMessage({ id: 'tariffDetails.chargingPriceTitle' })}
                    </div>
                    <div css={tariffDetailsStyles.rows}>
                        {[...tieredRows, ...minimalAmountRow].map((r, idx) => (
                            <div key={idx} css={tariffDetailsStyles.row}>
                                <div css={tariffDetailsStyles.name}>{r.name}</div>
                                <div css={tariffDetailsStyles.value}>{r.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : chargingPriceRows.length > 0 ? (
                <div>
                    <div css={tariffDetailsStyles.blockTitle}>
                        {intl.formatMessage({ id: 'tariffDetails.chargingPriceTitle' })}
                    </div>
                    <div css={tariffDetailsStyles.rows}>
                        {[...chargingPriceRows, ...minimalAmountRow].map((r, idx) => (
                            <div key={idx} css={tariffDetailsStyles.row}>
                                <div css={tariffDetailsStyles.name}>{r.name}</div>
                                <div css={tariffDetailsStyles.value}>{r.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {additionalFeesRows.length > 0 && (
                <div css={tariffDetailsStyles.sectionGap}>
                    <div css={tariffDetailsStyles.blockTitle}>
                        {intl.formatMessage({ id: 'tariffDetails.additionalFeesTitle' })}
                    </div>
                    <div css={tariffDetailsStyles.rows}>
                        {additionalFeesRows.map((r, idx) => (
                            <div key={idx} css={tariffDetailsStyles.row}>
                                <div css={tariffDetailsStyles.name}>{r.name}</div>
                                <div css={tariffDetailsStyles.value}>{r.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

